import { useInfiniteQuery } from '@tanstack/react-query';
import { Button, Label, Text } from '@gravity-ui/uikit';
import type { LabelProps } from '@gravity-ui/uikit';
import type { AdminAuditAction, AdminAuditEntry } from '@odonix/contracts';
import { useTenantsApi } from '../../lib/api/context.js';
import { moment } from '../../ui/format.js';
import { Page, Section } from '../../ui/page.js';
import { RouterLink } from '../../ui/router-link.js';
import { Empty, ErrorNote, LoadingRows } from '../../ui/states.js';
import { AUDIT_ACTION_LABEL } from '../tenants/labels.js';

/**
 * Журнал действий платформенной админки: кто, что и почему сделал с чужой
 * подпиской. Требование того же рода, что аудит доступа к карте пациента в
 * ядре (docs/02-architecture/09-SECURITY.md), только предмет другой — деньги
 * клиники, а не её медицинские данные.
 *
 * Блокировка и ограничение записи — красные: их ищут взглядом, а не чтением;
 * приём наличных — зелёный: это второе, что ищут в журнале.
 */

const PAGE_SIZE = 50;

const ACTION_THEME: Record<AdminAuditAction, NonNullable<LabelProps['theme']>> = {
  extend_trial: 'info',
  activate: 'success',
  manual_payment: 'success',
  block: 'danger',
  restrict: 'warning',
};

export function AuditLogScreen() {
  const api = useTenantsApi();

  const query = useInfiniteQuery({
    queryKey: ['audit'],
    queryFn: ({ pageParam }) => api.audit({ limit: PAGE_SIZE, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const entries = query.data?.pages.flatMap((page) => page.entries) ?? [];

  return (
    <Page title="Журнал действий" description="Каждое действие с подпиской клиники — с причиной, временем и тем, кто его сделал.">
      {query.isError ? (
        <ErrorNote title="Журнал не загрузился" error={query.error} onRetry={() => void query.refetch()} />
      ) : query.isPending ? (
        <LoadingRows rows={8} height={48} />
      ) : entries.length === 0 ? (
        <Empty title="Действий пока не было" />
      ) : (
        <>
          <Section flush>
            <ul className="flex flex-col divide-y divide-[var(--g-color-line-generic)]">
              {entries.map((entry) => (
                <EntryRow key={entry.id} entry={entry} />
              ))}
            </ul>
          </Section>
          {query.hasNextPage ? (
            <div className="flex justify-center">
              <Button view="outlined" loading={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
                Показать ещё
              </Button>
            </div>
          ) : null}
        </>
      )}
    </Page>
  );
}

function EntryRow({ entry }: { entry: AdminAuditEntry }) {
  return (
    <li className="flex flex-col gap-1 px-5 py-3 sm:flex-row sm:items-start sm:gap-4">
      <Text color="secondary" className="tabular w-40 shrink-0 pt-0.5" whiteSpace="nowrap">
        {moment(entry.occurredAt)}
      </Text>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <Label theme={ACTION_THEME[entry.action]} size="xs">
            {AUDIT_ACTION_LABEL[entry.action]}
          </Label>
          {entry.targetTenantName === null ? null : (
            <RouterLink to={`/tenants/${entry.targetTenantId}`}>{entry.targetTenantName}</RouterLink>
          )}
        </div>
        <Text className="max-w-[72ch] break-words">{entry.reason}</Text>
        <Text variant="caption-2" color="secondary">
          {entry.staffEmail}
        </Text>
      </div>
    </li>
  );
}
